import React, { useState, useEffect, useCallback } from 'react'
import Layout from '../components/Layout'
import { imageFolderService } from '../services/imageFolderService'
import { useApiError } from '../../shared/hooks/useApiError'
import MessageDialog from '../../shared/ui/MessageDialog'
import Button from '../../shared/ui/Button'
import { Trash2, Move, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Sortable Folder Item Component using @dnd-kit
const SortableFolderItem = ({ folder, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: folder.imageFolderId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="bg-white border-gray-300 border rounded p-3 flex items-center space-x-3"
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="w-10 h-10 bg-gray-700 rounded flex items-center justify-center cursor-move"
      >
        <Move className="w-5 h-5 text-white" />
      </div>

      {/* Folder Name Input */}
      <input
        type="text"
        value={folder.imageFolderName}
        onChange={(e) => onUpdate(folder.imageFolderId, 'imageFolderName', e.target.value)}
        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium h-10"
        placeholder="画像フォルダ名"
      />

      {/* Delete Button */}
      <button
        onClick={() => onDelete(folder.imageFolderId)}
        className="w-10 h-10 bg-red-600 rounded flex items-center justify-center hover:bg-red-700 transition-colors"
        title="削除"
      >
        <Trash2 className="w-4 h-4 text-white flex-shrink-0" />
      </button>
    </li>
  )
}

const ImageFolder = () => {
    const [existingImageFolderList, setExistingImageFolderList] = useState([]) // Existing folders with IDs
    const [newImageFolderList, setNewImageFolderList] = useState([]) // New folders without IDs
    const [deletedImageFolderList, setDeletedImageFolderList] = useState([]) // Deleted folders
    const [isLoading, setIsLoading] = useState(false)
    const [isRegistering, setIsRegistering] = useState(false)
    const [showDeletedFolders, setShowDeletedFolders] = useState(false)
    const [newImageFolder, setNewImageFolder] = useState({
        imageFolderName: ''
    })
    const [maxImageFolderId, setMaxImageFolderId] = useState(0)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')

    // Error handling
    const { showErrorDialog, errorMessage, handleApiError, handleApiResponse, closeErrorDialog } = useApiError()

    // @dnd-kit sensors with activation constraint to prevent accidental drags
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Combine all active folders for drag and drop
    const allActiveImageFolders = [...existingImageFolderList, ...newImageFolderList]

    // Handle drag end for reordering
    const handleDragEnd = (event) => {
        const { active, over } = event

        if (active.id !== over?.id && over) {
            const oldIndex = allActiveImageFolders.findIndex(item => item.imageFolderId === active.id)
            const newIndex = allActiveImageFolders.findIndex(item => item.imageFolderId === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(allActiveImageFolders, oldIndex, newIndex)
                
                // Add order index to each item
                const orderedList = newOrder.map((folder, index) => ({
                    ...folder,
                    orderIndex: index + 1
                }))
                
                // Separate the reordered list back into existing and new
                const existing = orderedList.filter(folder => !newImageFolderList.some(newFolder => newFolder.imageFolderId === folder.imageFolderId))
                const newOnes = orderedList.filter(folder => newImageFolderList.some(newFolder => newFolder.imageFolderId === folder.imageFolderId))
                
                setExistingImageFolderList(existing)
                setNewImageFolderList(newOnes)
            }
        }
    }

    const loadImageFolderList = useCallback(async () => {
        try {
            setIsLoading(true)
            const response = await imageFolderService.getImageFolderList(true)

            if (handleApiResponse(response, '画像フォルダ一覧の取得に失敗しました。')) {
                return
            }

            const allFolders = response?.imageFolderList || []
            const activeFolders = allFolders.filter(folder => folder.deleteFlg === '0' || folder.deleteFlg === 0)
            const deletedFolders = allFolders.filter(folder => folder.deleteFlg === '1' || folder.deleteFlg === 1)

            // Add order index to existing folders
            const activeFoldersWithOrder = activeFolders.map((folder, index) => ({
                ...folder,
                orderIndex: index + 1
            }))
            
            setExistingImageFolderList(activeFoldersWithOrder)
            setNewImageFolderList([]) // Clear new folders after loading
            setDeletedImageFolderList(deletedFolders)

            // Find max ID for new folders
            const maxId = Math.max(...allFolders.map(f => f.imageFolderId || 0), 0)
            setMaxImageFolderId(maxId)

        } catch (error) {
            console.error('Failed to load image folder list:', error)
            handleApiError(error, '画像フォルダ一覧の読み込みに失敗しました。')
        } finally {
            setIsLoading(false)
        }
    }, [handleApiResponse, handleApiError])

    useEffect(() => {
        loadImageFolderList()
    }, [loadImageFolderList])

    const handleAddImageFolder = () => {
        if (!newImageFolder.imageFolderName.trim()) {
            handleApiError(new Error('画像フォルダ名を入力してください。'), '入力エラー')
            return
        }

        const newImageFolderItem = {
            imageFolderId: maxImageFolderId + 1, // Use maxId + 1 for new folders
            imageFolderName: newImageFolder.imageFolderName,
            deleteFlg: '0',
            orderIndex: allActiveImageFolders.length + 1 // Add to the end of the list
        }

        setNewImageFolderList(prev => [...prev, newImageFolderItem])
        setMaxImageFolderId(prev => prev + 1)
        setNewImageFolder({ imageFolderName: '' })
    }

    const handleDeleteImageFolder = (imageFolderId) => {
        // Check if it's a new folder or existing folder
        const isNewFolder = newImageFolderList.some(folder => folder.imageFolderId === imageFolderId)
        
        if (isNewFolder) {
            // Remove from new folders list
            setNewImageFolderList(prev => prev.filter(folder => folder.imageFolderId !== imageFolderId))
        } else {
            // Remove from existing folders and add to deleted list
            setExistingImageFolderList(prev => prev.filter(folder => folder.imageFolderId !== imageFolderId))
            const deletedFolder = existingImageFolderList.find(folder => folder.imageFolderId === imageFolderId)
            if (deletedFolder) {
                setDeletedImageFolderList(prev => [...prev, { ...deletedFolder, deleteFlg: '1' }])
            }
        }
    }

    const handleRestoreImageFolder = (imageFolderId) => {
        setDeletedImageFolderList(prev => prev.filter(folder => folder.imageFolderId !== imageFolderId))
        const restoredFolder = deletedImageFolderList.find(folder => folder.imageFolderId === imageFolderId)
        if (restoredFolder) {
            setExistingImageFolderList(prev => [...prev, { ...restoredFolder, deleteFlg: '0' }])
        }
    }

    const handleUpdateImageFolder = (imageFolderId, field, value) => {
        // Check if it's a new folder or existing folder
        const isNewFolder = newImageFolderList.some(folder => folder.imageFolderId === imageFolderId)
        
        if (isNewFolder) {
            setNewImageFolderList(prev =>
                prev.map(folder =>
                    folder.imageFolderId === imageFolderId
                        ? { ...folder, [field]: value }
                        : folder
                )
            )
        } else {
            setExistingImageFolderList(prev =>
                prev.map(folder =>
                    folder.imageFolderId === imageFolderId
                        ? { ...folder, [field]: value }
                        : folder
                )
            )
        }
    }

    const handleRegister = async () => {
        try {
            setIsRegistering(true)

            // Process each list separately
            const promises = []

            // Add new folders
            if (newImageFolderList.length > 0) {
                promises.push(imageFolderService.addImageFolderList(newImageFolderList))
            }

            // Update existing folders
            if (existingImageFolderList.length > 0) {
                promises.push(imageFolderService.updateImageFolderList(existingImageFolderList))
            }

            // Remove deleted folders
            if (deletedImageFolderList.length > 0) {
                promises.push(imageFolderService.removeImageFolderList(deletedImageFolderList))
            }

            // Execute all operations
            if (promises.length > 0) {
                const responses = await Promise.all(promises)
                
                // Check for errors in any response
                for (const response of responses) {
                    if (handleApiResponse(response, '画像フォルダの登録に失敗しました。')) {
                        return
                    }
                }
            }

            // Show success message
            setSuccessMessage('登録が完了しました。')
            setShowSuccessDialog(true)
            
            // Clear new folders list and reload data
            setNewImageFolderList([])
            await loadImageFolderList()

        } catch (error) {
            console.error('Failed to register image folders:', error)
            handleApiError(error, '画像フォルダの登録に失敗しました。')
        } finally {
            setIsRegistering(false)
        }
    }


    return (
        <Layout pageTitle='画像フォルダ管理'>
            <div className="bg-white p-6 mx-6 my-6 max-w-4xl mx-auto">

                {isLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        {/* Active Folders List */}
                        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={allActiveImageFolders.map(folder => folder.imageFolderId)}
                                >
                                    <ul className="space-y-2">
                                        {allActiveImageFolders.map((folder) => (
                                            <SortableFolderItem
                                                key={folder.imageFolderId}
                                                folder={folder}
                                                onUpdate={handleUpdateImageFolder}
                                                onDelete={handleDeleteImageFolder}
                                            />
                                        ))}
                                    </ul>
                                </SortableContext>
                            </DndContext>
                        </div>

                        {/* Add New Folder Section */}
                        <div className="bg-pink-50 border border-gray-300 rounded-lg p-4 mb-6">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="text"
                                    value={newImageFolder.imageFolderName}
                                    onChange={(e) => setNewImageFolder(prev => ({ ...prev, imageFolderName: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium h-10"
                                    placeholder="画像フォルダ名"
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddImageFolder()
                                        }
                                    }}
                                />
                                <Button
                                    onClick={handleAddImageFolder}
                                    variant="secondary"
                                    size="md"
                                >
                                    追加
                                </Button>
                            </div>
                        </div>

                        {/* Register Button */}
                        <div className="text-center mb-6">
                            <Button
                                onClick={handleRegister}
                                variant="danger"
                                size="lg"
                                loading={isRegistering}
                                disabled={isRegistering}
                            >
                                登録する
                            </Button>
                        </div>

                        {/* Deleted Folders Section */}
                        {deletedImageFolderList.length > 0 && (
                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => setShowDeletedFolders(!showDeletedFolders)}
                                    className="w-full bg-gray-700 text-white px-4 py-3 flex items-center justify-between hover:bg-gray-800 transition-colors"
                                >
                                    <span>削除済画像フォルダ</span>
                                    {showDeletedFolders ? (
                                        <ChevronUp className="w-5 h-5" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5" />
                                    )}
                                </button>

                                {showDeletedFolders && (
                                    <div className="bg-white p-4">
                                        <ul className="space-y-2">
                                            {deletedImageFolderList.map((folder) => (
                                                <li
                                                    key={folder.imageFolderId}
                                                    className="bg-gray-50 border border-gray-200 rounded p-3 flex items-center justify-between"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900">{folder.imageFolderName}</div>
                                                    </div>
                                                    <Button
                                                        onClick={() => handleRestoreImageFolder(folder.imageFolderId)}
                                                        variant="outline"
                                                        size="sm"
                                                        icon={RotateCcw}
                                                    >
                                                        戻す
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Error Dialog */}
                <MessageDialog
                    isOpen={showErrorDialog}
                    onClose={closeErrorDialog}
                    title="エラー"
                    message={errorMessage}
                    type="error"
                />

                {/* Success Dialog */}
                <MessageDialog
                    isOpen={showSuccessDialog}
                    onClose={() => setShowSuccessDialog(false)}
                    title="成功"
                    message={successMessage}
                    type="success"
                />
            </div>
        </Layout>
    )
}

export default ImageFolder
